import { captureException, withScope } from "@sentry/react";
import { useLocation } from "@tanstack/react-router";
import { AlertTriangle, Home, RefreshCw, Wifi, WifiOff } from "lucide-react";
import React, { Component, type ReactNode } from "react";
import { useAuth } from "../contexts/AuthContext";
import { classifyError, ErrorType, getRetryDelay, shouldAutoRetry, type ClassifiedError } from "../lib/errorTypes";
import { Button } from "./ui/button";
import { CenteredCard } from "./ui/centered-card";

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    classifiedError: ClassifiedError | null;
    retryAttempts: number;
    isRetrying: boolean;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    pathname?: string;
    user?: { id: string; email?: string } | null;
}

interface ErrorFallbackProps {
    classifiedError: ClassifiedError | null;
    retryAttempts: number;
    isRetrying: boolean;
    resetError: () => void;
}

function ErrorFallback({ classifiedError, retryAttempts, isRetrying, resetError }: ErrorFallbackProps) {
    if (!classifiedError) {
        return (
            <CenteredCard>
                <div className="text-center space-y-4">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold">Something went wrong</h2>
                    <p className="text-muted-foreground max-w-md">We're sorry, but something unexpected happened.</p>
                    <Button onClick={resetError} className="w-full">
                        Try Again
                    </Button>
                </div>
            </CenteredCard>
        );
    }

    const getIcon = () => {
        switch (classifiedError.type) {
            case ErrorType.ChunkLoadError:
                return <RefreshCw className="w-16 h-16 text-primary animate-spin-slow" />;
            case ErrorType.NetworkError:
                return <WifiOff className="w-16 h-16 text-destructive" />;
            case ErrorType.APIError:
                return <Wifi className="w-16 h-16 text-warning" />;
            default:
                return <AlertTriangle className="w-16 h-16 text-muted-foreground" />;
        }
    };

    const getTitle = () => {
        switch (classifiedError.type) {
            case ErrorType.ChunkLoadError:
                return "App Update Available";
            case ErrorType.NetworkError:
                return "Connection Issue";
            case ErrorType.APIError:
                return "Server Issue";
            default:
                return "Something Went Wrong";
        }
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        window.location.href = "/";
    };

    return (
        <CenteredCard>
            <div className="text-center space-y-6">
                <div className="flex justify-center mb-4">{getIcon()}</div>

                <div className="space-y-2">
                    <h2 className="text-2xl font-bold">{getTitle()}</h2>
                    <p className="text-muted-foreground max-w-md">{classifiedError.message}</p>
                </div>

                {retryAttempts > 0 && (
                    <div className="text-sm text-muted-foreground">
                        Attempted {retryAttempts} time{retryAttempts > 1 ? "s" : ""}
                    </div>
                )}

                <div className="space-y-3">
                    {classifiedError.requiresReload ? (
                        <Button onClick={handleReload} className="w-full" disabled={isRetrying}>
                            {isRetrying ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Refreshing...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh Page
                                </>
                            )}
                        </Button>
                    ) : (
                        <Button onClick={resetError} className="w-full" disabled={isRetrying}>
                            {isRetrying ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Retrying...
                                </>
                            ) : (
                                "Try Again"
                            )}
                        </Button>
                    )}

                    <Button variant="outline" onClick={handleGoHome} className="w-full" disabled={isRetrying}>
                        <Home className="w-4 h-4 mr-2" />
                        Go Home
                    </Button>
                </div>

                {process.env.NODE_ENV === "development" && (
                    <details className="mt-4 text-left">
                        <summary className="cursor-pointer text-sm text-muted-foreground">Developer Info</summary>
                        <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto">
                            Type: {classifiedError.type}
                            {"\n"}
                            Retryable: {classifiedError.isRetryable ? "Yes" : "No"}
                            {"\n"}
                            Requires Reload: {classifiedError.requiresReload ? "Yes" : "No"}
                            {"\n"}
                            Message: {classifiedError.originalError.message}
                            {"\n"}
                            Stack: {classifiedError.originalError.stack}
                        </pre>
                    </details>
                )}
            </div>
        </CenteredCard>
    );
}

class ErrorBoundaryCore extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private retryTimeoutId: NodeJS.Timeout | null = null;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            classifiedError: null,
            retryAttempts: 0,
            isRetrying: false,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        const classifiedError = classifyError(error);
        return {
            hasError: true,
            error,
            classifiedError,
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error("AppErrorBoundary caught an error:", error, errorInfo);

        // Report to Sentry with additional context
        withScope((scope) => {
            scope.setTag("errorBoundary", "AppErrorBoundary");

            // Add page context
            if (this.props.pathname) {
                scope.setTag("page", this.props.pathname);
                scope.setContext("page", {
                    pathname: this.props.pathname,
                });
            }

            // Add user context
            if (this.props.user) {
                scope.setUser({
                    id: this.props.user.id,
                    email: this.props.user.email,
                });
            }

            scope.setContext("errorInfo", {
                componentStack: errorInfo.componentStack,
            });
            scope.setContext("classifiedError", {
                type: this.state.classifiedError?.type,
                isRetryable: this.state.classifiedError?.isRetryable,
                requiresReload: this.state.classifiedError?.requiresReload,
            });
            captureException(error);
        });

        // Auto-retry for certain error types
        if (this.state.classifiedError && shouldAutoRetry(this.state.classifiedError) && this.state.retryAttempts < 3) {
            this.scheduleRetry();
        }
    }

    componentWillUnmount() {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }
    }

    scheduleRetry = () => {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }

        this.setState({ isRetrying: true });

        const delay = getRetryDelay(this.state.retryAttempts + 1);

        this.retryTimeoutId = setTimeout(() => {
            this.setState((prevState) => ({
                retryAttempts: prevState.retryAttempts + 1,
                isRetrying: false,
            }));

            this.resetError();
        }, delay);
    };

    resetError = () => {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }

        this.setState({
            hasError: false,
            error: null,
            classifiedError: null,
            isRetrying: false,
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorFallback
                    classifiedError={this.state.classifiedError}
                    retryAttempts={this.state.retryAttempts}
                    isRetrying={this.state.isRetrying}
                    resetError={this.resetError}
                />
            );
        }

        return this.props.children;
    }
}

// Wrapper component that provides hooks data to the class component
export function AppErrorBoundary({ children }: { children: ReactNode }) {
    const location = useLocation();
    const { user } = useAuth();

    return (
        <ErrorBoundaryCore pathname={location.pathname} user={user}>
            {children}
        </ErrorBoundaryCore>
    );
}

interface ComponentErrorBoundaryProps {
    children: ReactNode;
    componentName?: string;
    fallback?: (props: { classifiedError: ClassifiedError | null; resetError: () => void }) => React.ReactElement;
}

export class ComponentErrorBoundary extends Component<ComponentErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ComponentErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            classifiedError: null,
            retryAttempts: 0,
            isRetrying: false,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        const classifiedError = classifyError(error);
        return { hasError: true, error, classifiedError };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error(`ComponentErrorBoundary (${this.props.componentName}) caught an error:`, error, errorInfo);

        // Report to Sentry with additional context
        withScope((scope) => {
            scope.setTag("errorBoundary", "ComponentErrorBoundary");
            scope.setTag("componentName", this.props.componentName || "unknown");
            scope.setContext("errorInfo", {
                componentStack: errorInfo.componentStack,
            });
            scope.setContext("classifiedError", {
                type: this.state.classifiedError?.type,
                isRetryable: this.state.classifiedError?.isRetryable,
                requiresReload: this.state.classifiedError?.requiresReload,
            });
            captureException(error);
        });
    }

    resetError = () => {
        this.setState({
            hasError: false,
            error: null,
            classifiedError: null,
            retryAttempts: 0,
            isRetrying: false,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback({
                    classifiedError: this.state.classifiedError,
                    resetError: this.resetError,
                });
            }

            const errorMessage = this.state.classifiedError?.message || "Something went wrong in this section";

            return (
                <div className="p-4 border border-destructive/20 bg-destructive/10 rounded-lg">
                    <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                        <span className="text-sm text-destructive font-medium">{errorMessage}</span>
                    </div>
                    {process.env.NODE_ENV === "development" && this.state.classifiedError && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            {this.props.componentName}: {this.state.classifiedError.type}
                        </div>
                    )}
                    <Button size="sm" variant="outline" onClick={this.resetError} className="mt-3 text-xs">
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Retry
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
